import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { EventOddValue } from "@/pages/Sport/utils/formatOddsRows";
import {
  allowedModes,
  systemBetCount,
  systemPayout,
  systemTypesFor,
} from "@/pages/Sport/utils/slipModes";
import { BetSelection, BetSlipState, BetType } from "@/types";

const calcTotals = (
  bets: BetSelection[],
  betType: BetType,
  systemType: string,
): { totalStake: number; potentialWinnings: number } => {
  if (bets.length === 0) return { totalStake: 0, potentialWinnings: 0 };

  if (betType === "single") {
    const totalStake = bets.reduce((sum, bet) => sum + bet.stake, 0);
    const potentialWinnings = bets.reduce((sum, bet) => sum + bet.stake * Number(bet.odds), 0);

    return { totalStake, potentialWinnings };
  }

  const stake = bets[0]?.stake ?? 0;
  if (betType === "system") {
    return {
      totalStake: stake * systemBetCount(systemType, bets.length),
      potentialWinnings: systemPayout(systemType, bets, stake),
    };
  }

  // A builder's price is the product less Betaxis' correlation factor, known on placement.
  const combinedOdds = bets.reduce((product, bet) => product * Number(bet.odds), 1);

  return { totalStake: stake, potentialWinnings: stake * combinedOdds };
};

/** Falls back to singles when the picks no longer allow the mode, and keeps the system type valid. */
const normalizeMode = (state: BetSlipState) => {
  if (!allowedModes(state.bets).includes(state.betType)) state.betType = "single";
  if (state.betType === "system") {
    const types = systemTypesFor(state.bets.length);
    if (!types.includes(state.systemType)) state.systemType = types[0] ?? "";
  }
  // A boosted price is a single bet on its own.
  if (state.bets.some((bet) => bet.boostId) && state.bets.length > 1) {
    state.bets = state.bets.filter((bet) => !bet.boostId);
  }
};

const refresh = (state: BetSlipState) => {
  normalizeMode(state);
  Object.assign(state, calcTotals(state.bets, state.betType, state.systemType));
};

const initialState: BetSlipState = {
  bets: [],
  betType: "single",
  systemType: "",
  grantId: null,
  totalStake: 0,
  potentialWinnings: 0,
  isOpen: false,
};

const betSlipSlice = createSlice({
  name: "betSlip",
  initialState,
  reducers: {
    addBet(state, action: PayloadAction<Omit<BetSelection, "stake">>) {
      const index = state.bets.findIndex((bet) => bet.id === action.payload.id);

      if (index !== -1) {
        state.bets.splice(index, 1);
      } else {
        // A boost goes on the slip alone; adding to a boosted slip replaces the boost.
        if (action.payload.boostId) state.bets = [];
        else state.bets = state.bets.filter((bet) => !bet.boostId);
        state.bets.push({ ...action.payload, stake: 0 });
        state.isOpen = true;
      }

      refresh(state);
    },

    removeBet(state, action: PayloadAction<string>) {
      state.bets = state.bets.filter((bet) => bet.id !== action.payload);
      refresh(state);
    },

    updateStake(state, action: PayloadAction<{ id: string; stake: number }>) {
      const stake = Math.max(0, action.payload.stake);

      if (state.betType !== "single") {
        state.bets.forEach((bet) => {
          bet.stake = stake;
        });
      } else {
        const bet = state.bets.find((b) => b.id === action.payload.id);
        if (bet) bet.stake = stake;
      }

      refresh(state);
    },

    setBetType(state, action: PayloadAction<BetType>) {
      if (state.betType !== action.payload) {
        state.betType = action.payload;
        state.bets.forEach((bet) => {
          bet.stake = 0;
        });
      }
      refresh(state);
    },

    setSystemType(state, action: PayloadAction<string>) {
      state.systemType = action.payload;
      refresh(state);
    },

    /** A free bet or profit boost for the slip, or none. */
    setGrant(state, action: PayloadAction<string | null>) {
      state.grantId = action.payload;
    },

    /**
     * The latest prices for picks already in the slip. A pick whose price
     * moved keeps the price it was made at beside the new one until the
     * player accepts; one that moved back is as it was.
     */
    syncOdds(state, action: PayloadAction<{ id: string; odds: EventOddValue }[]>) {
      let touched = false;
      for (const { id, odds } of action.payload) {
        const bet = state.bets.find((b) => b.id === id);
        // A boost shows its own price, not the market's.
        if (!bet || bet.boostId || Number(bet.odds) === Number(odds)) continue;

        if (bet.oddsChanged && Number(bet.previousOdds) === Number(odds)) {
          delete bet.previousOdds;
          delete bet.oddsChanged;
        } else {
          if (!bet.oddsChanged) bet.previousOdds = bet.odds;
          bet.oddsChanged = true;
        }
        bet.odds = odds;
        touched = true;
      }
      if (touched) refresh(state);
    },

    /** Which picks on screen are suspended right now; a pick off screen keeps its last state. */
    syncLocks(state, action: PayloadAction<{ id: string; locked: boolean }[]>) {
      for (const { id, locked } of action.payload) {
        const bet = state.bets.find((b) => b.id === id);
        if (!bet || bet.boostId) continue;
        if (locked) bet.locked = true;
        else delete bet.locked;
      }
    },

    removeLockedBets(state) {
      state.bets = state.bets.filter((bet) => !bet.locked);
      refresh(state);
    },

    acceptOddsChanges(state) {
      state.bets.forEach((bet) => {
        delete bet.previousOdds;
        delete bet.oddsChanged;
      });
    },

    openBetSlip(state) {
      state.isOpen = true;
    },
    closeBetSlip(state) {
      state.isOpen = false;
    },
    toggleBetSlip(state) {
      state.isOpen = !state.isOpen;
    },

    clearBetSlip(state) {
      state.bets = [];
      state.betType = "single";
      state.systemType = "";
      state.grantId = null;
      state.totalStake = 0;
      state.potentialWinnings = 0;
    },
  },
});

export default betSlipSlice.reducer;

export const {
  addBet,
  removeBet,
  updateStake,
  setBetType,
  setSystemType,
  setGrant,
  syncOdds,
  syncLocks,
  removeLockedBets,
  acceptOddsChanges,
  openBetSlip,
  closeBetSlip,
  toggleBetSlip,
  clearBetSlip,
} = betSlipSlice.actions;
